import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * EXCEL CATALOG DIALOG
 * Dosya seç → dry-run (önizleme) → onayla → uygula
 * Beklenen Excel sütunları: ÜRÜN_ADI | BARKOD | GRUBU | FİYAT | ADET | KG | MARKA
 */
export default function ExcelCatalogDialog({ open, onClose, onApplied }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setBusy(false); if (fileRef.current) fileRef.current.value = ""; };

  const close = () => { reset(); onClose && onClose(); };

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
  };

  const doDryRun = async () => {
    if (!file) { toast.error("Önce bir Excel dosyası seç"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/products/excel-replace?confirm=false", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dosya okunamadı");
    } finally {
      setBusy(false);
    }
  };

  const doApply = async () => {
    if (!file || !preview) return;
    const summary = `${preview.will_create} yeni + ${preview.will_update} güncelle + ${preview.will_deactivate} PASİFLEŞTİR`;
    const warnBlock = (preview.warnings || []).filter((w) => w.severity === "warning").map((w) => `\n⚠️ ${w.message}`).join("");
    if (!window.confirm(`⚠️ KATALOG DEĞİŞTİRİLECEK:\n${summary}${warnBlock}\n\nBu işlem Excel'de olmayan ürünleri siteden gizler. Emin misin?`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = (preview.warnings || []).some((w) => w.severity === "warning")
        ? "/admin/products/excel-replace?confirm=true&force=true"
        : "/admin/products/excel-replace?confirm=true";
      const r = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data?.needs_force) {
        toast.error("İşlem onay gerektiriyor — tekrar denemek için 'Onayla ve Uygula'ya bas.");
        return;
      }
      toast.success(`✓ ${r.data.created} yeni · ${r.data.updated} güncelle · ${r.data.deactivated} pasif`);
      if (onApplied) await onApplied();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uygulanamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="bg-[#FAF7F2] border-[#E8E2D5] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="excel-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#D9653B]" />
            Excel'den Katalog Yükle
          </DialogTitle>
          <DialogDescription className="text-xs text-[#1B2A22]/70">
            Beklenen sütunlar: <b>ÜRÜN_ADI · BARKOD · GRUBU · FİYAT · ADET · KG · MARKA</b>
            <br />💡 <b>Arşiv formatı</b> da kabul edilir: <code>STOKKODU · STOK_ADI · BARKOD · SAYILAN_MIKTAR</code> (sayım bitince havuzdan indirdiğin "Arşivim" Excel'ini doğrudan yükleyebilirsin).
            <br />Excel'de olmayan ürünler <b>siteden gizlenir</b> (DB'de kalır — Wolvox sync sonra geri getirebilir).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Step 1: File picker */}
          <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => fileRef.current?.click()}
                variant="outline"
                className="rounded-full border-[#2E5A36] text-[#2E5A36] font-heading font-bold"
                data-testid="excel-pick-btn"
                disabled={busy}
              >
                <Upload className="w-4 h-4 mr-2" />
                {file ? "Dosyayı değiştir" : "Excel seç (.xlsx / .csv)"}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={pick} data-testid="excel-file-input" />
              {file && (
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-xs text-[#1B2A22] truncate" data-testid="excel-filename">{file.name}</p>
                  <p className="text-[10px] text-[#1B2A22]/50">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              )}
            </div>
            {file && !preview && (
              <Button
                onClick={doDryRun}
                disabled={busy}
                className="mt-3 w-full h-10 rounded-full bg-[#2E5A36] hover:bg-[#24472B] text-white font-heading font-bold"
                data-testid="excel-preview-btn"
              >
                {busy ? "Analiz ediliyor..." : "👁 Önce Önizle (uygulamaz)"}
              </Button>
            )}
          </div>

          {/* Step 2: Preview */}
          {preview && (
            <div className="bg-white border border-[#D9653B] rounded-2xl p-4 space-y-3" data-testid="excel-preview">
              <h4 className="font-heading font-bold text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#D9653B]" />
                Önizleme — henüz uygulanmadı
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                <StatBox label="Satır" value={preview.received_rows} color="#1B2A22" />
                <StatBox label="Yeni" value={preview.will_create} color="#2E5A36" />
                <StatBox label="Güncelle" value={preview.will_update} color="#E5A024" />
                <StatBox label="Pasif" value={preview.will_deactivate} color="#D9653B" />
              </div>
              {typeof preview.final_active_estimate === "number" && (
                <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
                  <div className="bg-[#1B2A22]/5 rounded-lg p-2">
                    <div className="text-[10px] text-[#1B2A22]/60">Şu an aktif</div>
                    <div className="font-mono font-bold">{preview.active_before}</div>
                  </div>
                  <div className="bg-[#2E5A36]/10 rounded-lg p-2">
                    <div className="text-[10px] text-[#2E5A36]">İşlem sonrası aktif</div>
                    <div className="font-mono font-extrabold text-[#2E5A36]">{preview.final_active_estimate}</div>
                  </div>
                  <div className="bg-[#D9653B]/10 rounded-lg p-2">
                    <div className="text-[10px] text-[#D9653B]">Pasife alınacak oran</div>
                    <div className="font-mono font-bold text-[#D9653B]">%{preview.will_hide_percent}</div>
                  </div>
                </div>
              )}
              {Array.isArray(preview.warnings) && preview.warnings.length > 0 && (
                <div className="space-y-1.5">
                  {preview.warnings.map((w, i) => (
                    <div
                      key={i}
                      className={`text-[11px] rounded-lg px-3 py-2 border-l-4 ${w.severity === "critical" ? "bg-red-50 border-red-500 text-red-800" : "bg-[#E5A024]/10 border-[#E5A024] text-[#B4711C]"}`}
                      data-testid={`excel-warning-${w.code}`}
                    >
                      <b>{w.severity === "critical" ? "⛔ KRİTİK:" : "⚠️ UYARI:"}</b> {w.message}
                    </div>
                  ))}
                </div>
              )}
              {preview.skipped_rows > 0 && (
                <p className="text-[11px] text-[#1B2A22]/60">
                  ⚠️ {preview.skipped_rows} satır atlandı (eksik ad veya fiyat).
                </p>
              )}
              {preview.sample_create?.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[#2E5A36] mb-1">Örnek yeni ürünler:</p>
                  <ul className="text-[11px] font-mono text-[#1B2A22]/70 space-y-0.5">
                    {preview.sample_create.map((r, i) => (
                      <li key={i}>+ {r.name} · {r.barcode || "—"} · ₺{r.price}</li>
                    ))}
                  </ul>
                </div>
              )}
              {preview.sample_deactivate?.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[#D9653B] mb-1">Örnek pasifleşecekler:</p>
                  <ul className="text-[11px] font-mono text-[#1B2A22]/70 space-y-0.5">
                    {preview.sample_deactivate.map((r, i) => (
                      <li key={i}>- {r.name} · {r.barcode || "—"}</li>
                    ))}
                  </ul>
                </div>
              )}
              <Button
                onClick={doApply}
                disabled={busy || (preview.warnings || []).some((w) => w.severity === "critical")}
                className="w-full h-11 rounded-full bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold disabled:bg-gray-400"
                data-testid="excel-apply-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {busy ? "Uygulanıyor..." : (preview.warnings || []).some((w) => w.severity === "critical") ? "⛔ Uygulanamaz" : "✓ ONAYLA ve UYGULA"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="rounded-xl border border-[#E8E2D5] p-2">
      <div className="text-[10px] uppercase tracking-wider text-[#1B2A22]/60">{label}</div>
      <div className="font-mono font-extrabold text-xl" style={{ color }}>{value}</div>
    </div>
  );
}
